import React, { useState, useEffect } from 'react';
import {
  Select, InputNumber, DatePicker, Input, Button, Table,
  message, Popconfirm, Typography, Space, Card,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import apiService from '../../services/api';

const { Title } = Typography;
const { Option } = Select;

const EXPENSE_TYPES = ['Insurance', 'Other'];

const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);

  const [type, setType] = useState(null);
  const [amount, setAmount] = useState(null);
  const [date, setDate] = useState(dayjs());
  const [notes, setNotes] = useState('');

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const res = await apiService.expenses.getAll();
      setExpenses(res.data || []);
    } catch {
      message.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchExpenses(); }, []);

  const handleAdd = async () => {
    if (!type) return message.warning('Please select an expense type');
    if (!amount || amount <= 0) return message.warning('Please enter a valid amount');
    try {
      setAdding(true);
      await apiService.expenses.create({
        type,
        amount,
        date: date.toISOString(),
        notes,
      });
      setType(null);
      setAmount(null);
      setDate(dayjs());
      setNotes('');
      fetchExpenses();
    } catch {
      message.error('Failed to add expense');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiService.expenses.delete(id);
      setExpenses(prev => prev.filter(e => e._id !== id));
    } catch {
      message.error('Failed to delete');
    }
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 130,
      render: (d) => dayjs(d).format('DD MMM YYYY'),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 130,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      align: 'right',
      render: (v) => `£${Number(v).toFixed(2)}`,
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      render: (v) => v || '—',
    },
    {
      title: '',
      key: 'action',
      width: 60,
      align: 'center',
      render: (_, record) => (
        <Popconfirm title="Delete this expense?" onConfirm={() => handleDelete(record._id)} okText="Delete" okType="danger">
          <Button type="text" danger icon={<DeleteOutlined />} size="small" />
        </Popconfirm>
      ),
    },
  ];

  const total = expenses.reduce((s, e) => s + (e.amount || 0), 0);

  return (
    <div>
      <Title level={3} style={{ marginBottom: 20 }}>Expenses</Title>

      {/* Add form */}
      <Card style={{ marginBottom: 24 }}>
        <Space wrap>
          <Select
            placeholder="Expense type"
            value={type}
            onChange={setType}
            style={{ width: 160 }}
          >
            {EXPENSE_TYPES.map(t => <Option key={t} value={t}>{t}</Option>)}
          </Select>
          <InputNumber
            placeholder="Amount (£)"
            value={amount}
            onChange={setAmount}
            min={0}
            precision={2}
            prefix="£"
            style={{ width: 150 }}
          />
          <DatePicker
            value={date}
            onChange={(d) => d && setDate(d)}
            format="DD MMM YYYY"
            allowClear={false}
          />
          <Input
            placeholder="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            style={{ width: 220 }}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            loading={adding}
          >
            Add
          </Button>
        </Space>
      </Card>

      {/* List */}
      <Table
        dataSource={expenses}
        columns={columns}
        rowKey="_id"
        loading={loading}
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
        size="small"
        summary={() => expenses.length > 0 && (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={2}><strong>Total</strong></Table.Summary.Cell>
            <Table.Summary.Cell index={2} align="right"><strong>£{total.toFixed(2)}</strong></Table.Summary.Cell>
            <Table.Summary.Cell index={3} colSpan={2} />
          </Table.Summary.Row>
        )}
      />
    </div>
  );
};

export default Expenses;
